import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthPageShell from './components/AuthPageShell';
import { resetForgottenPassword } from './utils/apiAuth';
import { validateNewPassword, passwordErrorMessage } from './utils/passwordRules';

function RequestAnotherLink() {
  return (
    <Link
      to="/forgotten-password"
      className="mt-6 block w-full h-12 rounded-lg bg-primary px-6 text-base font-bold text-white hover:bg-primary/90 transition-colors text-center leading-[3rem]"
    >
      Request a new link
    </Link>
  );
}

function ResetPassword() {
  const [searchParams] = useSearchParams();
  // Opaque — pass it back exactly as it arrived, no trimming or decoding.
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  // A spent, expired or unknown token all come back as one 400 and all mean
  // the same thing to the person holding the link: ask for another.
  const [linkDead, setLinkDead] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const invalid = validateNewPassword(newPassword, confirmation);
    if (invalid) {
      setFieldError(invalid);
      return;
    }

    setLoading(true);
    setError('');
    setFieldError('');

    try {
      await resetForgottenPassword(token, newPassword, confirmation);
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        setLinkDead(true);
      } else if (status === 422) {
        setFieldError(passwordErrorMessage(err, 'That password was rejected.'));
      } else {
        setError(passwordErrorMessage(err, 'Could not set the new password. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  // No token in the URL — there is nothing to submit, so say so rather than
  // letting the user fill in a form that cannot work.
  if (!token || linkDead) {
    return (
      <AuthPageShell title="This link is not valid">
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">
            {token
              ? 'This reset link is no longer valid — it may have expired, or already been used.'
              : 'This page needs a reset link from your email.'}
          </p>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Reset links last 12 hours and work once. Ask for a fresh one and it will arrive in a
          moment.
        </p>
        <RequestAnotherLink />
      </AuthPageShell>
    );
  }

  if (done) {
    return (
      <AuthPageShell title="Password changed">
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-sm text-green-700 dark:text-green-300">
            Your password has been changed. Sign in with it to continue.
          </p>
        </div>
        <Link
          to="/login"
          className="block w-full h-12 rounded-lg bg-primary px-6 text-base font-bold text-white hover:bg-primary/90 transition-colors text-center leading-[3rem]"
        >
          Go to login
        </Link>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell title="Set a new password">
      <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
        Choose a new password for your account. It must be at least 8 characters.
      </p>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label
            htmlFor="newPassword"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            New password
          </label>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            disabled={loading}
            autoComplete="new-password"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        <div>
          <label
            htmlFor="confirmation"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Confirm new password
          </label>
          <input
            type="password"
            id="confirmation"
            name="confirmation"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            required
            disabled={loading}
            autoComplete="new-password"
            className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-background-dark text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
              fieldError ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
          />
          {fieldError && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldError}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex h-12 items-center justify-center rounded-lg bg-primary px-6 text-base font-bold text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Saving...' : 'Set new password'}
        </button>
      </form>
    </AuthPageShell>
  );
}

export default ResetPassword;
